import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { Box, Card, Container, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import TestBlock from 'pages/VocabularyTest/components/TestBlock';
import StatusActionsBar from 'pages/VocabularyTest/components/StatusActionsBar';
import StageNav from 'pages/VocabularyTest/components/StageNav';
import CategoryNav, {
  RailStrip,
} from 'pages/VocabularyTest/components/CategoryNav';
import FinishTestDialog from 'pages/VocabularyTest/components/FinishTestDialog';
import { VocabularyTestWord, WordStatusServer } from 'app/types';
import { vocabularyTestActions } from 'pages/VocabularyTest/store/vocabularyTest.slice';
import WithLoader from 'components/WithLoader';
import FilledActionButton from 'components/FilledActionButton';
import {
  applyStatusToSelectedWords,
  DESKTOP_LAYOUT_BREAKPOINT,
  getVocabularyTestTopicKey,
  getVocabularyTestWordKey,
} from 'pages/VocabularyTest/utils';

interface ITestInProgressProps {}

// AppHeader is NOT sticky on this page (see AppHeader.tsx's isTestRunPage
// check) — it scrolls away with the page, so this bar has the top of the
// viewport to itself once scrolled past it.
const STICKY_TOP_OFFSET = 0;
// Height of the sticky status bar below the header — used both as the
// CategoryNav's sticky offset and as the scroll-jump target offset.
const STATUS_BAR_HEIGHT = 82;

const TestInProgress: React.FC<ITestInProgressProps> = () => {
  const dispatch = useAppDispatch();
  const dataInitializedRef = useRef(false);
  const { loading, data: vocabularyTestInProgress } = useAppSelector(
    (store) => store.vocabularyTest.vocabularyTestInProgress,
  );
  const { data: testTopics } = useAppSelector(
    (store) => store.vocabularyTest.vocabularyTestTopics,
  );

  const [data, setData] = useState(vocabularyTestInProgress);
  const [selectedWords, setSelectedWords] = useState<
    Record<string, boolean | undefined>
  >({});
  const [finishDialogOpen, setFinishDialogOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(true);
  const [activeCategoryKey, setActiveCategoryKey] = useState<string | null>(
    null,
  );
  const [hideStatusLabel, setHideStatusLabel] = useState(false);

  useEffect(() => {
    setData(vocabularyTestInProgress);
    setSelectedWords({});

    if (dataInitializedRef.current) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      dataInitializedRef.current = true;
    }
  }, [vocabularyTestInProgress]);

  const passedTopicsCount = testTopics.filter(
    ({ status }) => status === 'PASSED',
  ).length;
  const currentTopicsCount = testTopics.filter(
    ({ status }) => status === 'CURRENT',
  ).length;
  const completedAndCurrentTopicsCount = passedTopicsCount + currentTopicsCount;
  const progress = testTopics.length
    ? Math.round((completedAndCurrentTopicsCount / testTopics.length) * 100)
    : 0;
  const canGoPrevious = passedTopicsCount > 0;
  const canGoNext = testTopics.some(({ status }) => status === 'UPCOMING');

  const goPrevious = () => dispatch(vocabularyTestActions.getPreviousWords());
  const goNext = () => {
    if (!canGoNext) return;
    dispatch(vocabularyTestActions.saveAndGetNext(data));
  };

  const confirmFinish = () => {
    setFinishDialogOpen(false);
    dispatch(vocabularyTestActions.finishTest(data));
  };

  const selectedCount = Object.values(selectedWords).filter(Boolean).length;

  const toggleWordSelection = (word: VocabularyTestWord) => {
    const key = getVocabularyTestWordKey(word);
    setSelectedWords((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
  };

  const toggleGroupSelection = (words: VocabularyTestWord[]) => {
    const allSelected = words.every(
      (word) => !!selectedWords[getVocabularyTestWordKey(word)],
    );
    setSelectedWords((prev) => {
      const next = { ...prev };
      words.forEach((word) => {
        const key = getVocabularyTestWordKey(word);
        if (allSelected) delete next[key];
        else next[key] = true;
      });
      return next;
    });
  };

  const applyStatus = (status: WordStatusServer) => {
    if (selectedCount === 0) return;
    setData((prev) => applyStatusToSelectedWords(prev, selectedWords, status));
    setSelectedWords({});
  };

  // Single scroll handler driving two things at once:
  //  1. Fade the status bar's instructional label out as soon as the page
  //     has scrolled at all — visible only at the very top (the buttons
  //     themselves stay put; only the label text hides).
  //  2. Highlight whichever category card is under the sticky bars.
  // The sidebar itself is plain CSS position:sticky (see CategoryNav) — no
  // JS involved in positioning it, so it tracks scroll natively without lag.
  useEffect(() => {
    const categoryKeys = data.map(getVocabularyTestTopicKey);

    const handleScroll = () => {
      const currentY = window.scrollY;

      // Visible only at the very top of the page — hidden the moment there's
      // any scroll at all, in either direction (not just while scrolling
      // down), and only comes back once scrolled all the way back to 0.
      setHideStatusLabel(currentY > 0);

      if (categoryKeys.length) {
        const scrollSpyThreshold = STICKY_TOP_OFFSET + STATUS_BAR_HEIGHT + 40;
        let current = categoryKeys[0];
        for (const key of categoryKeys) {
          const el = document.querySelector(`[data-cat="${key}"]`);
          if (
            el instanceof HTMLElement &&
            el.getBoundingClientRect().top <= scrollSpyThreshold
          ) {
            current = key;
          }
        }
        setActiveCategoryKey(current);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [data]);

  const jumpToCategory = (key: string) => {
    const el = document.querySelector(`[data-cat="${key}"]`);
    if (!(el instanceof HTMLElement)) return;
    const top =
      el.getBoundingClientRect().top +
      window.scrollY -
      (STICKY_TOP_OFFSET + STATUS_BAR_HEIGHT + 16);
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const stageNavProps = {
    canGoNext,
    canGoPrevious,
    onFinish: () => setFinishDialogOpen(true),
    onNext: goNext,
    onPrevious: goPrevious,
    progress,
    testTopics,
  };

  return (
    <React.Fragment>
      <Card
        sx={(theme) => ({
          // Card defaults to boxShadow:'none' + no border (theme.ts). This
          // sticky action bar needs to read clearly as a distinct panel
          // floating above the content scrolling under it, so give it a
          // primary-tinted border + a deeper shadow (more explicit than the
          // plain neutral drop shadow the header uses).
          border: '1px solid',
          borderColor: alpha(theme.palette.primary.main, 0.22),
          boxShadow: `0 10px 28px -12px ${alpha(
            theme.palette.common.black,
            0.45,
          )}`,
          mb: { xs: 1.5, md: 2.5 },
          position: 'sticky',
          px: { xs: 1.5, md: 3 },
          py: { xs: 1, md: 2 },
          top: STICKY_TOP_OFFSET,
          // Highest-priority element on the run page: it must overlap
          // everything that scrolls under it, including the "Прогрес
          // тестування" dropdown (ProgressTopicsDropdown's Popper is pinned
          // to appBar). Kept below the true overlay tiers (drawer/modal/
          // snackbar) so dialogs like FinishTestDialog still cover it.
          zIndex: theme.zIndex.appBar + 1,
        })}
      >
        <Stack
          alignItems={'center'}
          direction={'row'}
          spacing={1.5}
          sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
        >
          <Box
            sx={(theme) => ({
              display: 'none',
              flex: 1,
              [theme.breakpoints.up(DESKTOP_LAYOUT_BREAKPOINT)]: {
                display: 'block',
              },
            })}
          />
          <StatusActionsBar
            hideLabel={hideStatusLabel}
            onApplyStatus={applyStatus}
            onClearSelection={() => setSelectedWords({})}
            selectedCount={selectedCount}
          />
          {/* Завершити тест lives here on desktop; on phone it moves down
              into StageNav (its own row, above Попередні/Наступні) — there's
              no room for it alongside the status buttons at that width. */}
          <Box
            sx={(theme) => ({
              display: 'none',
              flex: 1,
              justifyContent: 'flex-end',
              [theme.breakpoints.up(DESKTOP_LAYOUT_BREAKPOINT)]: {
                display: 'flex',
              },
            })}
          >
            <FilledActionButton
              onClick={() => setFinishDialogOpen(true)}
              sx={{ flex: 'none', height: 42 }}
            >
              {'Завершити тест'}
            </FilledActionButton>
          </Box>
        </Stack>
      </Card>

      <WithLoader loading={loading}>
        <Container>
          <StageNav {...stageNavProps} />

          <Stack
            direction={'row'}
            sx={{ alignItems: 'flex-start', gap: 3.5 }}
          >
            {/* Category sidebar is desktop-only (see its own
                DESKTOP_LAYOUT_BREAKPOINT display toggle — not a wrapping Box
                here, which would become the sticky element's containing
                block and collapse it to its own short content height
                instead of the row's) — on
                phone/tablet there's no room for a persistent rail, so
                navigation between categories in the current batch is by
                scrolling (with StageNav's prev/next handling batch changes
                as usual). */}
            {railOpen ? (
              <CategoryNav
                activeKey={activeCategoryKey}
                categories={data}
                onCollapse={() => setRailOpen(false)}
                onJump={jumpToCategory}
                stickyTop={STICKY_TOP_OFFSET + STATUS_BAR_HEIGHT + 16}
              />
            ) : (
              <RailStrip
                onClick={() => setRailOpen(true)}
                stickyTop={STICKY_TOP_OFFSET + STATUS_BAR_HEIGHT + 16}
              />
            )}

            <Stack sx={{ flex: 1, minWidth: 0 }}>
              {data.map((test) => (
                <TestBlock
                  key={getVocabularyTestTopicKey(test)}
                  onToggleGroup={toggleGroupSelection}
                  onToggleWord={toggleWordSelection}
                  railOpen={railOpen}
                  selectedWords={selectedWords}
                  testData={test}
                />
              ))}
            </Stack>
          </Stack>

          <Box sx={{ mb: 5 }}>
            <StageNav {...stageNavProps} />
          </Box>
        </Container>
      </WithLoader>

      <FinishTestDialog
        categoriesDone={passedTopicsCount}
        categoriesTotal={testTopics.length}
        onClose={() => setFinishDialogOpen(false)}
        onConfirm={confirmFinish}
        open={finishDialogOpen}
      />
    </React.Fragment>
  );
};

export default TestInProgress;
